import { Button, Input } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import {
  AutosizeTextarea,
  ChoiceSelect,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  HelpPopover
} from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { useAuth } from '@/domains/auth';
import { ddApi } from '../../api/dd-api';
import type { DdMeetingKind, IDdAccount } from '../../api/dd-contracts';
import { listsApi } from '../../api/lists-api';
import { useAdminData } from '../../components/admin-page';
import { fromLocalInput, meetingLabel, toLocalInput } from '../../dd';
import { REMOVED_ACCOUNT } from '../../lists';
import { refusalMessage } from '../../refusal';

/**
 * *Schedule…* (SR-525, A2): one dialog for every kind of DD meeting — the kind, the date and
 * time, who will hold it and a note. The DD call is booked on this cycle's interview
 * (`PUT dd-call-schedule`, which opens one where there is none) with its interviewer, who then
 * finds it in My tasks; any other kind opens a new meeting, booked and assigned in the same write.
 * The same dialog moves a booking (*Reschedule…*): the DD call's through the same `PUT`, which can
 * also clear it, a meeting's through its sheet's save under the revision the sheet holds — its
 * note with it (`scheduleNote`), opened on the one held so a move does not lose it. It
 * judges no date — a past one is a meeting booked and logged late.
 */

/** A booking as held: its date, who holds it, and the note it was set with. */
export interface IDdBooking {
  at: Nullable<string>;
  interviewer: Nullable<IDdAccount>;
  note: Nullable<string>;
}

/** A meeting's booking moved from its sheet: the save, which answers null when it landed. */
export interface IMeetingReschedule extends IDdBooking {
  kind: DdMeetingKind;
  save: (
    scheduledAt: string,
    interviewerAccountId: Nullable<string>,
    scheduleNote: Nullable<string>
  ) => Promise<Nullable<string>>;
}

/** survey-api's `no-live-response` to a meeting's booking or logging, said; null for any other. */
export function noCycleRefusal(error: unknown): Nullable<string> {
  return error instanceof ApiError && error.isProblem(ProblemType.noLiveResponse)
    ? 'There is no response to this survey, so no cycle to hold a meeting in.'
    : null;
}

/** The value the interviewer box holds for nobody: no account id spells it. */
export const NOBODY = '';

/** The staff a meeting may be assigned to — the Lists assignee picker's accounts, the DD calls
 *  queue's too — and a held interviewer no longer among them, as spelled. `nobody` offers none. */
export function useInterviewerChoices(
  needed: boolean,
  held: Nullable<IDdAccount>,
  nobody = true
): { value: string; label: string }[] {
  const accounts = useAdminData(async (signal) => (needed ? listsApi.assignees(signal) : null), [needed]);
  const listed = (accounts.data ?? []).map((account) => ({ value: account.accountId, label: account.email }));
  const missing =
    held && !listed.some((choice) => choice.value === held.accountId)
      ? [{ value: held.accountId, label: held.email ?? REMOVED_ACCOUNT }]
      : [];
  return [...(nobody ? [{ value: NOBODY, label: 'Nobody' }] : []), ...missing, ...listed];
}

export function ScheduleDialog({
  participantId,
  surveyCode,
  kinds,
  kind: firstKind,
  call,
  meeting,
  onClose,
  onDone,
  onChanged
}: {
  participantId: string;
  surveyCode: string;
  /** The kinds offered — one draws no kind box. */
  kinds: ReadonlyArray<{ value: DdMeetingKind; label: string }>;
  kind: DdMeetingKind;
  /** The DD call's booking, which choosing the DD call opens the form on — null while none. */
  call: Nullable<IDdBooking>;
  /** A meeting's booking being moved, its kind fixed. */
  meeting?: IMeetingReschedule;
  onClose: () => void;
  /** The write landed: the host reads again and closes. */
  onDone: () => void;
  /** A refusal saying the block moved under the press — a completed call — read again. */
  onChanged?: () => void;
}) {
  const { user } = useAuth();
  const formId = React.useId();
  const kindId = React.useId();
  const interviewerId = React.useId();
  const viewer = user?.accountId ?? NOBODY;
  const openOn = (next: DdMeetingKind): IDdBooking | null => meeting ?? (next === 'dd_call' ? call : null);
  // A booking moved keeps its interviewer, or nobody where it has none — never the one moving it;
  // a new one is the viewer's unless an interviewer is already set.
  const interviewerOf = (booking: IDdBooking | null): string =>
    booking?.interviewer?.accountId ?? (booking?.at ? NOBODY : viewer);
  const first = openOn(firstKind);
  const [kind, setKind] = React.useState<DdMeetingKind>(meeting?.kind ?? firstKind);
  const [at, setAt] = React.useState(toLocalInput(first?.at ?? null));
  const [interviewer, setInterviewer] = React.useState<string>(interviewerOf(first));
  const [note, setNote] = React.useState(first?.note ?? '');
  const [busy, setBusy] = React.useState(false);
  const [said, setSaid] = React.useState<Nullable<string>>(null);

  const isCall = kind === 'dd_call';
  const held = openOn(kind);
  // A DD call's interviewer is kept where none is sent, so "Nobody" cannot take one off.
  const choices = useInterviewerChoices(true, held?.interviewer ?? null, !(isCall && held?.interviewer));

  const choose = (next: string) => {
    const nextKind = next as DdMeetingKind;
    const was = openOn(kind);
    setKind(nextKind);
    const booking = openOn(nextKind);
    if (booking) {
      setAt(toLocalInput(booking.at));
      setNote(booking.note ?? '');
      setInterviewer(interviewerOf(booking));
    } else {
      // A new meeting starts blank: nothing of the DD call's booking carried over.
      setAt('');
      setNote('');
      if (was) setInterviewer(viewer);
    }
  };

  const refusal = (error: unknown): string => {
    const noCycle = noCycleRefusal(error);
    if (noCycle) return noCycle;
    if (error instanceof ApiError && error.isProblem(ProblemType.invalidTransition)) {
      onChanged?.();
      const reason = error.problem?.reason;
      return typeof reason === 'string' ? reason : 'The DD call is completed.';
    }
    return refusalMessage(error, isCall ? 'The call could not be scheduled.' : 'The meeting could not be scheduled.');
  };

  const run = async (send: () => Promise<Nullable<string>>) => {
    setBusy(true);
    setSaid(null);
    try {
      const refused = await send();
      if (refused) setSaid(refused);
      else onDone();
    } catch (error) {
      setSaid(refusal(error));
    } finally {
      setBusy(false);
    }
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const scheduledAt = fromLocalInput(at);
    if (!scheduledAt) return;
    const who = interviewer === NOBODY ? null : interviewer;
    const booked = note.trim() || null;
    void run(async () => {
      if (meeting) return meeting.save(scheduledAt, who, booked);
      if (isCall) await ddApi.scheduleCall(participantId, surveyCode, scheduledAt, booked, who);
      else {
        await ddApi.openMeeting(participantId, {
          survey: surveyCode,
          kind,
          scheduledAt,
          interviewerAccountId: who,
          note: booked
        });
      }
      return null;
    });
  };

  const clear = () =>
    void run(async () => {
      await ddApi.scheduleCall(participantId, surveyCode, null);
      return null;
    });
  const moving = meeting !== undefined || (isCall && call?.at);

  return (
    <Dialog open onOpenChange={(next) => !next && !busy && onClose()}>
      <DialogContent data-dd-schedule="">
        <DialogHeader>
          <DialogTitle>{meeting ? `Reschedule ${meetingLabel(kind)}` : 'Schedule a meeting'}</DialogTitle>
        </DialogHeader>
        <DialogBody as="form" id={formId} onSubmit={submit}>
          {kinds.length > 1 && !meeting && (
            <div className="grid gap-1 text-[0.8125rem]">
              <span id={kindId}>Meeting</span>
              <ChoiceSelect labelledBy={kindId} value={kind} onChange={choose} choices={kinds} disabled={busy} />
            </div>
          )}
          <label className="grid gap-1 text-[0.8125rem]">
            Date and time
            <Input type="datetime-local" required value={at} onChange={(event) => setAt(event.target.value)} disabled={busy} />
          </label>
          <div className="grid gap-1 text-[0.8125rem]">
            <span className="flex items-center gap-1">
              <span id={interviewerId}>Interviewer</span>
              <HelpPopover label="About the interviewer">
                <p>The meeting goes to its interviewer’s My tasks, with the note.</p>
              </HelpPopover>
            </span>
            <ChoiceSelect
              labelledBy={interviewerId}
              value={interviewer}
              onChange={setInterviewer}
              choices={choices}
              disabled={busy}
            />
          </div>
          <label className="grid gap-1 text-[0.8125rem]">
            Note <span className="sr-only">(optional)</span>
            <AutosizeTextarea
              minRows={2}
              value={note}
              maxLength={2000}
              placeholder="Optional — where it is held, who arranged it"
              onChange={(event) => setNote(event.target.value)}
              disabled={busy}
            />
          </label>
          {said && (
            <p role="alert" className="text-[0.8125rem]">
              {said}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          {isCall && call?.at && !meeting && (
            <Button key="clear" type="button" variant="outline" className="mr-auto" disabled={busy} onClick={clear}>
              Clear the scheduled call
            </Button>
          )}
          <Button key="cancel" type="button" variant="outline" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button key="schedule" type="submit" form={formId} disabled={busy || at === ''}>
            {busy ? 'Saving…' : moving ? 'Save' : 'Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
